/**
 * "Candidato responsável": which rival this adversary account speaks for, inside this campaign
 * (`GET`/`PUT .../tracked-accounts/{id}/candidate`). Offers the campaign's own opponent
 * alliances first (`useAlliances`, already loaded for the record's "Aliados e Adversários"),
 * then falls back to a live TSE search over the campaign's first election
 * (`../../tse/api/queries`'s `useTseCandidates`, the same search `TseCandidatePicker` drives),
 * plus "Nenhum / não se aplica".
 *
 * ponytail: the TSE half searches the campaign's first election with no UF filter, unlike
 * `TseCandidatePicker`'s full Cargo/Estado picker -- add office/UF narrowing if a large roster
 * makes the plain search noisy.
 */
import { useId, useState, type ReactNode } from 'react';

import { Avatar, AvatarFallback, AvatarImage } from '../../../../ui/avatar';
import { Button } from '../../../../ui/button';
import { Command, CommandGroup, CommandItem, CommandList } from '../../../../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../ui/popover';
import { Skeleton } from '../../../../ui/skeleton';
import { useAlliances } from '../../record/api/queries';
import { useElections, useTseCandidates } from '../../tse/api/queries';
import { useAccountCandidate, useWriteAccountCandidate } from '../../adversaries/api/queries';
import type { Candidate } from '../../adversaries/api/schemas';
import { am } from '../messages';

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return (
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts.at(-1)?.[0] ?? '') : '')
  ).toUpperCase();
}

export function CandidateAvatar(props: { candidate: Candidate; className?: string }): ReactNode {
  const name = props.candidate.name ?? '?';
  return (
    <Avatar className={props.className ?? 'size-7'}>
      {props.candidate.photo_url !== null && (
        <AvatarImage
          src={props.candidate.photo_url}
          alt={am('candidate.photo', { name })}
          className="object-cover"
        />
      )}
      <AvatarFallback className="text-caption">{initialsOf(name)}</AvatarFallback>
    </Avatar>
  );
}

export function CandidateSelect(props: {
  campaignId: string;
  tenantId: string;
  trackedAccountId: string;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const current = useAccountCandidate(props.campaignId, props.trackedAccountId);
  const write = useWriteAccountCandidate(props.campaignId, props.trackedAccountId);
  const alliances = useAlliances(props.campaignId);
  const elections = useElections(props.tenantId);
  const electionId = elections.data?.[0]?.id ?? '';
  const [query, setQuery] = useState('');
  const searching = query.trim().length >= 2;
  const tseCandidates = useTseCandidates(props.tenantId, {
    electionId,
    office: null,
    electoralUnit: null,
    ballotNumber: null,
    q: query,
  });
  const labelId = useId();

  const opponents = (alliances.data ?? []).filter(
    (item) => item.relation === 'opponent' && item.kind === 'candidate',
  );

  function pick(write_: {
    named_entity_id: string | null;
    tse_election_id: string | null;
    tse_registration_id: string | null;
  }): void {
    write.mutate(write_);
    setOpen(false);
    setQuery('');
  }

  return (
    <div className="flex flex-col gap-1">
      <span id={labelId} className="text-xs font-medium text-muted-foreground">
        {am('candidate.label')}
      </span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-fit justify-start gap-2"
            aria-labelledby={labelId}
            disabled={current.isPending || write.isPending}
          >
            {current.data !== undefined && current.data.name !== null ? (
              <>
                <CandidateAvatar candidate={current.data} className="size-5" />
                {current.data.name}
              </>
            ) : (
              am('candidate.none')
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-0" aria-label={am('candidate.label')}>
          <Command shouldFilter={false}>
            <CommandList>
              <CommandGroup heading={am('candidate.clear')}>
                <CommandItem
                  value="__none__"
                  onSelect={() =>
                    pick({
                      named_entity_id: null,
                      tse_election_id: null,
                      tse_registration_id: null,
                    })
                  }
                >
                  {am('candidate.none')}
                </CommandItem>
              </CommandGroup>
              {opponents.length > 0 && (
                <CommandGroup heading={am('candidate.fromAlliances')}>
                  {opponents.map((option) => (
                    <CommandItem
                      key={`${option.tse_election_id ?? ''}-${option.tse_registration_id ?? option.named_entity_id ?? option.name}`}
                      value={option.name}
                      onSelect={() =>
                        pick({
                          named_entity_id: option.named_entity_id,
                          tse_election_id: option.tse_election_id,
                          tse_registration_id: option.tse_registration_id,
                        })
                      }
                      className="gap-2"
                    >
                      {option.photo_url !== null && (
                        <Avatar className="size-6">
                          <AvatarImage src={option.photo_url} alt="" className="object-cover" />
                          <AvatarFallback>{initialsOf(option.name)}</AvatarFallback>
                        </Avatar>
                      )}
                      {option.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              <CommandGroup heading={am('candidate.search')}>
                <CommandItem value="__search_input__" disabled className="p-0">
                  <input
                    type="text"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={am('candidate.search.placeholder')}
                    aria-label={am('candidate.search.placeholder')}
                    className="w-full border-0 bg-transparent px-2 py-1.5 text-sm outline-none"
                  />
                </CommandItem>
                {searching && tseCandidates.isPending && (
                  <CommandItem value="__loading__" disabled>
                    <Skeleton className="h-8 w-full" />
                  </CommandItem>
                )}
                {searching && tseCandidates.isSuccess && tseCandidates.data.length === 0 && (
                  <CommandItem value="__empty__" disabled>
                    {am('candidate.search.empty')}
                  </CommandItem>
                )}
                {searching &&
                  tseCandidates.isSuccess &&
                  tseCandidates.data.map((registration) => (
                    <CommandItem
                      key={registration.tse_id}
                      value={registration.tse_id}
                      onSelect={() =>
                        pick({
                          named_entity_id: null,
                          tse_election_id: registration.election_id,
                          tse_registration_id: registration.tse_id,
                        })
                      }
                    >
                      <span className="font-medium">{registration.ballot_number}</span>
                      {registration.ballot_name}
                    </CommandItem>
                  ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
